import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';
import { AuthNotificationService } from './auth-notification.service';
import { catchError, switchMap, throwError, take } from 'rxjs';
import { AUTH_CONFIG } from '@app/core/config/auth.config';
import { AuthErrorResponse } from '@app/types/auth.interface';

/**
 * Flag para evitar loops infinitos en el refresh de tokens.
 * Asegura que una request no se reintente más de una vez.
 */
const RETRY_HEADER = 'X-Auth-Retry-Attempted';

/**
 * Interceptor HTTP para manejo de autenticación JWT.
 *
 * Funcionalidades:
 * - Agrega automáticamente el header Authorization a todas las requests
 * - Detecta errores 401 con token expirado y realiza refresh automático
 * - Reintenta la request original con el nuevo token
 * - Maneja requests concurrentes evitando múltiples refreshes simultáneos
 * - Evita loops infinitos con un mecanismo de retry único
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const notificationService = inject(AuthNotificationService);

  // ===== 1. SKIP AUTH HEADER =====
  // Si la petición tiene el header Skip-Auth, no añadir Bearer token
  if (req.headers.has('Skip-Auth')) {
    const cleanReq = req.clone({
      headers: req.headers.delete('Skip-Auth'),
    });
    console.log('🔓 AuthInterceptor - Skipping auth header for:', req.url);
    return next(cleanReq);
  }

  // ===== 2. EXCLUDED ROUTES =====
  // No agregar token a endpoints de autenticación (login, refresh)
  const isExcludedRoute = AUTH_CONFIG.EXCLUDED_ROUTES.some((route) => req.url.includes(route));

  if (isExcludedRoute) {
    console.log('🔓 AuthInterceptor - Excluded route:', req.url);
    return next(req);
  }

  // ===== 3. ADD AUTHORIZATION HEADER =====
  const token = authService.getAccessToken();
  let authReq = req;

  if (token) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
    console.log('🔐 AuthInterceptor - Added auth token to request:', req.url);
  } else {
    console.log('⚠️ AuthInterceptor - No token available for:', req.url);
  }

  // ===== 4. SEND REQUEST AND HANDLE ERRORS =====
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Solo manejar errores 401 (Unauthorized)
      if (error.status !== 401) {
        return throwError(() => error);
      }

      console.log('🚫 AuthInterceptor - 401 error received:', req.url);

      // ===== 5. PREVENT INFINITE LOOPS =====
      // Si esta request ya intentó refresh una vez, no reintentar
      if (req.headers.has(RETRY_HEADER)) {
        console.log('❌ AuthInterceptor - Retry already attempted, failing request');
        authService.logout();
        return throwError(() => error);
      }

      // ===== 6. CHECK ERROR TYPE =====
      const errorBody = error.error as AuthErrorResponse;
      const errorCode = errorBody?.error;

      console.log('🔍 AuthInterceptor - Error code:', errorCode);

      // Si el error NO es token_expired, no intentar refresh
      if (errorCode !== AUTH_CONFIG.ERROR_CODES.TOKEN_EXPIRED) {
        console.log('❌ AuthInterceptor - Not a token_expired error, logging out');

        // Mostrar notificación específica según el tipo de error
        if (errorCode) {
          notificationService.showAuthError(errorCode);
        }

        // Manejar otros tipos de errores
        switch (errorCode) {
          case AUTH_CONFIG.ERROR_CODES.TOKEN_INVALID:
            console.error('🔴 Invalid token, logging out');
            break;
          case AUTH_CONFIG.ERROR_CODES.NOT_AUTHENTICATED:
            console.error('🔴 Not authenticated, logging out');
            break;
          case AUTH_CONFIG.ERROR_CODES.AUTHENTICATION_ERROR:
            console.error('🔴 Authentication error');
            break;
        }

        authService.logout();
        return throwError(() => error);
      }

      // ===== 7. ATTEMPT TOKEN REFRESH =====
      console.log('🔄 AuthInterceptor - Token expired, attempting refresh...');

      return authService.refreshToken().pipe(
        take(1), // Solo tomar el primer valor
        switchMap((refreshResponse) => {
          console.log('✅ AuthInterceptor - Token refreshed, retrying original request');

          // ===== 8. RETRY ORIGINAL REQUEST =====
          // Clonar la request original con el nuevo token y el flag de retry
          const newAuthReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${refreshResponse.token}`,
              [RETRY_HEADER]: 'true',
            },
          });

          // Reintentar la request original
          return next(newAuthReq);
        }),
        catchError((refreshError) => {
          // ===== 9. REFRESH FAILED =====
          console.error('❌ AuthInterceptor - Token refresh failed:', refreshError);

          // El AuthService ya hace logout en caso de error, pero asegurarnos
          authService.logout();

          // Devolver el error original, no el de refresh
          return throwError(() => error);
        }),
      );
    }),
  );
};
